import { yupResolver } from '@hookform/resolvers/yup'
import { lazyLoad } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ControlledToggleButtonGroup,
  UncontrolledTextFieldDebounced,
  useTranslatedToggleButtonOptionEnum,
} from '@if/mui'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment, Toolbar, Typography } from '@mui/material'
import { ESystemMatchingResult, getESystemMatchingResultList } from '@src/types/interac'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export interface ManualInteracWireFiltersType {
  clientNameContains: string | null
  email: string | null
  systemMatchingResult: ESystemMatchingResult
}

enum ESearchType {
  ClientName = 'clientName',
  Email = 'email',
}
const getESearchTypeList = lazyLoad(() => Object.values(ESearchType))

interface UnmatchedInteracWireFiltersProps {
  onFiltersChange: (filters: ManualInteracWireFiltersType) => void
  defaultValues?: ManualInteracWireFiltersType | null
}

const UnmatchedInteracWireFilters = ({
  onFiltersChange,
  defaultValues: pDefaultValues,
}: UnmatchedInteracWireFiltersProps) => {
  const { t } = useTranslation()
  const resolver = useMemo(
    () =>
      yupResolver(
        yup.object({
          search: yup
            .string()
            .nullable()
            .when('searchType', {
              is: ESearchType.Email,
              then: (schema) => schema.email(),
            }),
          searchType: yup.mixed<ESearchType>().oneOf(Object.values(ESearchType)).default(ESearchType.ClientName),
          systemMatchingResult: yup
            .mixed<ESystemMatchingResult>()
            .oneOf(getESystemMatchingResultList())
            .default(ESystemMatchingResult.Sure),
        }),
      ),
    [],
  )
  const defaultValues = useMemo(
    () => ({
      search: pDefaultValues?.clientNameContains ?? pDefaultValues?.email ?? null,
      searchType: pDefaultValues?.email ? ESearchType.Email : ESearchType.ClientName,
      systemMatchingResult: pDefaultValues?.systemMatchingResult ?? ESystemMatchingResult.Sure,
    }),
    [pDefaultValues?.clientNameContains, pDefaultValues?.email, pDefaultValues?.systemMatchingResult],
  )

  const { subscribe, control } = useForm({
    mode: 'onChange',
    resolver,
    defaultValues,
  })

  useEffect(() => {
    const cancelSubscription = subscribe({
      formState: {
        isValid: true,
        values: true,
      },
      callback: ({ values, isValid }) => {
        if (isValid) {
          const filters: ManualInteracWireFiltersType = {
            systemMatchingResult: values.systemMatchingResult,
            clientNameContains: null,
            email: null,
          }
          if (values.search) {
            if (values.searchType === ESearchType.Email) {
              filters.email = values.search
            } else if (values.searchType === ESearchType.ClientName) {
              filters.clientNameContains = values.search
            }
          }
          onFiltersChange(filters)
        }
      },
    })
    return () => {
      cancelSubscription()
    }
  })

  const systemMatchingResultOptions = useTranslatedToggleButtonOptionEnum(
    'enum.systemMatchingResult',
    getESystemMatchingResultList(),
  )
  const searchTypeOptions = useTranslatedToggleButtonOptionEnum('enum.searchType', getESearchTypeList())

  return (
    <Toolbar sx={{ gap: 1, pb: 1, alignItems: 'start' }}>
      <div>
        <Typography variant="subtitle2" style={{ visibility: 'hidden' }}>
          &nbsp;
        </Typography>
        <UncontrolledTextFieldDebounced
          control={control}
          name="search"
          fullWidth={false}
          sx={{ width: 300 }}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </div>
      <div>
        <Typography variant="subtitle2">{t('common.searchType')}</Typography>
        <ControlledToggleButtonGroup
          control={control}
          name="searchType"
          exclusive
          color="primary"
          size="small"
          options={searchTypeOptions}
        />
      </div>
      <div>
        <Typography variant="subtitle2">{t('interacWire.matchLabel')}</Typography>
        <ControlledToggleButtonGroup
          control={control}
          name="systemMatchingResult"
          exclusive
          size="small"
          options={systemMatchingResultOptions}
          color="primary"
        />
      </div>
    </Toolbar>
  )
}

export default UnmatchedInteracWireFilters
